import { useState, type ReactNode } from "react"

import {
  Button,
  DateRangeCalendarContextProvider,
  Popover,
  PopoverContent,
  PopoverTrigger,
  type DateRangeCalendarContextValue,
} from "@coval/ui"
import { Calendar } from "lucide-react"

import {
  DateRangePanel,
  type DateRangeBounds,
} from "@/components/date-range/DateRangePanel"
import type { DatePreset } from "@/types/DatePreset"
import { formatDateRangeLabel } from "@/utils/date/dateRangeLabel"

export type { DateRangeBounds }

interface DateRangeFilterButtonProps<TPreset extends DatePreset> {
  /** Applied range: labels the trigger and fills the custom range inputs. */
  value: DateRangeBounds
  /** Receives every edit to the custom range inputs, including partial ones. */
  onRangeChange: (range: DateRangeBounds) => void
  /** Quick Select windows, offered above the custom range. */
  presets: TPreset[]
  /**
   * Applies a picked preset. Callers that persist the choice take this to keep
   * the preset's identity; the default applies the range the preset computes.
   */
  onPresetSelect?: (preset: TPreset) => void
  /** Omit to hide the clear action, on surfaces that always carry a range. */
  onClear?: () => void
  /**
   * Names the time field this filters on, for surfaces that filter on more
   * than one. The trigger then carries the field name instead of the applied
   * range, so two of these side by side stay tellable apart.
   */
  fieldLabel?: string
  /**
   * Labels the applied range on the trigger. Surfaces whose ranges usually
   * come from a preset pass one that recognises it, so the trigger reads
   * "Last 7 days" rather than the two dates it resolved to.
   */
  formatAppliedLabel?: (value: DateRangeBounds) => string | null
  /**
   * Outlines the trigger while a range is applied. For surfaces where the
   * range is an optional filter and its being set is worth flagging; surfaces
   * that always carry a range leave the trigger plain.
   */
  highlightWhenApplied?: boolean
  /**
   * Freezes the whole control — trigger, presets and range inputs. Surfaces
   * that persist a picked range use this while the write is in flight, so the
   * range that gets saved is the one still on screen when it lands.
   */
  dateOnly?: boolean
  disabled?: boolean
  /** Shown under the custom range inputs, e.g. why an edit was not applied. */
  customRangeError?: string | null
  /** Extra sections below the custom range, e.g. surface-level defaults. */
  renderExtraSections?: (closePopover: () => void) => ReactNode
  /**
   * Drives the popover from outside, for toolbars that keep at most one filter
   * popover open at a time. Omit to let the control own its open state.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Render the trigger as a compact icon button with its label in a tooltip. */
  iconOnly?: boolean
  /**
   * Data attributes for the popover content, e.g. the masking flags surfaces
   * over customer data put on anything the session recorders would capture.
   */
  contentDataAttributes?: Record<string, string>
  /** Optional presentation and visible-grid reporting for a feature variant. */
  calendarContext?: DateRangeCalendarContextValue
  calendarTimeZone?: string
}

/**
 * The app's date control: quick presets over a custom range, in a popover
 * behind a trigger that carries the applied range rather than a bare "Date".
 *
 * Date is the filter operators reach for on nearly every visit, so surfaces
 * put this in their toolbar rather than inside a filter dropdown, and every
 * surface shares one control so the presets and the label read the same.
 */
export function DateRangeFilterButton<TPreset extends DatePreset>({
  value,
  onRangeChange,
  presets,
  onPresetSelect,
  onClear,
  fieldLabel,
  formatAppliedLabel = formatDateRangeLabel,
  highlightWhenApplied = false,
  disabled = false,
  dateOnly = false,
  customRangeError,
  renderExtraSections,
  open,
  onOpenChange,
  iconOnly = false,
  contentDataAttributes,
  calendarContext,
  calendarTimeZone,
}: DateRangeFilterButtonProps<TPreset>) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isOpen = open ?? uncontrolledOpen
  const appliedLabel = formatAppliedLabel(value)
  const filterNoun = fieldLabel?.toLowerCase() ?? "date"

  const setIsOpen = (next: boolean) => {
    if (open === undefined) {
      setUncontrolledOpen(next)
    }
    onOpenChange?.(next)
  }

  const handlePresetSelect = (preset: TPreset) => {
    if (onPresetSelect) {
      onPresetSelect(preset)
    } else {
      onRangeChange(preset.getValue())
    }
    setIsOpen(false)
  }

  const handleClear = () => {
    onClear?.()
    setIsOpen(false)
  }

  const trigger = (
    <PopoverTrigger asChild disabled={disabled}>
      <Button
        type="button"
        variant="secondary"
        className="shrink-0"
        icon={iconOnly}
        active={highlightWhenApplied && appliedLabel !== null}
        // Named the same way in both modes: `icon` is a runtime choice, and the
        // label has to hold when the button is showing its text instead.
        aria-label={
          appliedLabel
            ? `Change ${filterNoun} range, currently ${appliedLabel}`
            : `Filter by ${fieldLabel ? filterNoun : "date range"}`
        }
        // The short name is what a tooltip is for; the accessible name above
        // carries the detail a screen reader needs.
        tooltip={iconOnly ? (fieldLabel ?? appliedLabel ?? "Date") : undefined}
        // Bottom: this row sits under the sticky navbar, which Radix does not treat as a collision edge.
        tooltipProps={{ side: "bottom" }}
      >
        <Calendar />
        {!iconOnly && (fieldLabel ?? appliedLabel ?? "Date")}
      </Button>
    </PopoverTrigger>
  )

  const panel = (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {trigger}
      <PopoverContent
        {...contentDataAttributes}
        side="bottom"
        align="start"
        collisionPadding={8}
        // Height follows the panel, so the clear action below the calendar
        // stays on screen. The cap is the space the popover actually has:
        // it only engages on a viewport too short to hold the panel at all,
        // where scrolling is what keeps that action reachable.
        className="w-80 p-0 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto"
      >
        <DateRangePanel
          value={value}
          onRangeChange={onRangeChange}
          presets={presets}
          onPresetSelect={handlePresetSelect}
          onClear={onClear && handleClear}
          clearLabel={fieldLabel ? `Clear ${fieldLabel}` : "Clear Date Range"}
          dateOnly={dateOnly}
          disabled={disabled}
          customRangeError={customRangeError}
          renderExtraSections={
            renderExtraSections &&
            (() => renderExtraSections(() => setIsOpen(false)))
          }
        />
      </PopoverContent>
    </Popover>
  )

  return calendarContext ? (
    <DateRangeCalendarContextProvider
      {...calendarContext}
      timeZone={calendarTimeZone ?? calendarContext.timeZone}
    >
      {panel}
    </DateRangeCalendarContextProvider>
  ) : (
    panel
  )
}
